import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  BulkConfirmResult,
  FilingConfirmBody,
  FilingConfirmed,
  FilingAnswer,
  FilingPage,
  FilingStatus,
  SenderTarget,
} from '../models/attachment-filing.model';

/**
 * The "To file" list (card 845): emails whose attachments matched no order, and the confirm,
 * dismiss and undo a person does on them. `openCount` is the N of "To file (N)" and the badge on
 * Emails in the top bar.
 */
@Injectable({ providedIn: 'root' })
export class AttachmentFilingService {
  private http = inject(HttpClient);
  private readonly apiUrl = `${environment.apiBaseUrl}/communications/attachments-to-file`;

  /** Emails waiting on "To file"; null until it is known (or for a user who cannot see the list) */
  readonly openCount = signal<number | null>(null);

  /** One page of a view; with a search only the emails it finds */
  list(status: FilingStatus, page = 1, pageSize = 25, search = ''): Observable<FilingPage> {
    let params = new HttpParams()
      .set('status', status)
      .set('page', page)
      .set('page_size', pageSize);
    if (search) params = params.set('search', search);
    return this.http
      .get<FilingPage>(`${this.apiUrl}/`, { params })
      .pipe(tap((answer) => this.takeCount(answer)));
  }

  /** Reads N again (after a confirm, a dismiss, an undo); a failed read keeps the last N */
  refreshOpenCount(): void {
    this.list('open', 1, 1).subscribe({ error: () => undefined });
  }

  confirm(id: number, body: FilingConfirmBody = {}): Observable<FilingConfirmed> {
    return this.http
      .post<FilingConfirmed>(`${this.apiUrl}/${id}/confirm/`, body)
      .pipe(tap((answer) => this.takeCount(answer)));
  }

  bulkConfirm(ids: number[]): Observable<BulkConfirmResult> {
    return this.http
      .post<BulkConfirmResult>(`${this.apiUrl}/bulk-confirm/`, { ids })
      .pipe(tap((answer) => this.takeCount(answer)));
  }

  dismiss(id: number): Observable<FilingAnswer> {
    return this.http
      .post<FilingAnswer>(`${this.apiUrl}/${id}/dismiss/`, {})
      .pipe(tap((answer) => this.takeCount(answer)));
  }

  undo(id: number): Observable<FilingAnswer> {
    return this.http
      .post<FilingAnswer>(`${this.apiUrl}/${id}/undo/`, {})
      .pipe(tap((answer) => this.takeCount(answer)));
  }

  /** N from an answer; one without it (an older backend) keeps the last N */
  private takeCount(answer: { open_count?: number }): void {
    if (typeof answer.open_count === 'number') this.openCount.set(answer.open_count);
  }

  /** Saves the sender's address on the customer or mill, so its next files file on their own */
  saveSender(
    id: number,
    target: Pick<SenderTarget, 'kind' | 'id'>
  ): Observable<{ saved: boolean }> {
    return this.http.post<{ saved: boolean }>(`${this.apiUrl}/${id}/save-sender/`, {
      kind: target.kind,
      id: target.id,
    });
  }
}
